import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import classNames from 'classnames';
import { Form as FinalForm } from 'react-final-form';

import { injectIntl } from '../../../util/reactIntl';
import {
  ExternalLink,
  FieldCurrencyInput,
  FieldSelect,
  FieldTextInput,
  Form,
  PrimaryButton,
} from '../../../components';
import { useConfiguration } from '../../../context/configurationContext';
import { composeValidators, minValue, required } from '../../../util/validators';
import { OWN } from '../../../util/constants';
import { getConfigOptions, getListingField } from '../../../util/genericHelpers';
import { ListingFieldConfigKey } from '../../../util/enums';
import { createFilterOptions } from '../../../components/CustomExtendedDataField/CustomExtendedDataField';
import appSettings from '../../../config/settings';

import css from './DimensionVerificationForm.module.css';

const DimensionVerificationFormComponent = props => {
  const config = useConfiguration();
  const marketplaceCurrency = config.currency;
  const configEnumOptions = getListingField(config, 'listing.listingFields', [
    ListingFieldConfigKey.UNIT_OF_MEASUREMENT,
  ]);
  const unitOfMeasurementEnumOptions = getConfigOptions(
    configEnumOptions,
    ListingFieldConfigKey.UNIT_OF_MEASUREMENT
  );
  const unitOfMeasurementOptions = createFilterOptions(unitOfMeasurementEnumOptions);

  return (
    <FinalForm
      {...props}
      render={fieldRenderProps => {
        const {
          className,
          rootClassName,
          disabled,
          handleSubmit,
          intl,
          invalid,
          values,
          ready,
          inProgress,
          shippingPreference,
        } = fieldRenderProps;

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = inProgress;
        const submitDisabled = invalid || disabled || submitInProgress || values?.weight == 0;
        const isOwnShipping = shippingPreference === OWN;

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            <div className={css.contactDetailsSection}>
              <div className={css.sectionContainer}>
                {/* <h3>
                  {intl.formatMessage({
                    id: 'DimensionVerificationForm.confirmTitle',
                  })}
                </h3> */}
                <p>
                  {intl.formatMessage(
                    {
                      id: 'DimensionVerificationForm.confirmMessage',
                    },
                    { lineBreak: <br /> }
                  )}
                </p>

                {isOwnShipping ? (
                  <div className={classNames(css.formRow, css.fullWidth)}>
                    <FieldCurrencyInput
                      id="shippingPriceInSubunitsOneItem"
                      name="shippingPriceInSubunitsOneItem"
                      label={intl.formatMessage({
                        id: 'EditListingProductForm.shippingOneItemLabel',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'EditListingProductForm.shippingOneItemPlaceholder',
                      })}
                      className={css.formFld}
                      currencyConfig={appSettings.getCurrencyFormatting(marketplaceCurrency)}
                      validate={required(
                        intl.formatMessage({
                          id: 'EditListingProductForm.shippingOneItemRequired',
                        })
                      )}
                      key="oneItemValidation"
                    />
                    <FieldCurrencyInput
                      id="shippingPriceInSubunitsAdditionalItems"
                      name="shippingPriceInSubunitsAdditionalItems"
                      className={css.formFld}
                      label={intl.formatMessage({
                        id: 'EditListingProductForm.shippingAdditionalItemsLabel',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'EditListingProductForm.shippingAdditionalItemsPlaceholder',
                      })}
                      currencyConfig={appSettings.getCurrencyFormatting(marketplaceCurrency)}
                      validate={required(
                        intl.formatMessage({
                          id: 'EditListingProductForm.shippingAdditionalItemsRequired',
                        })
                      )}
                      key="additionalItemsValidation"
                    />
                  </div>
                ) : (
                  <div className={css.weightSec}>
                    <div className={classNames(css.formRow, css.fullWidth)}>
                      <FieldTextInput
                        className={css.formFld}
                        type="number"
                        id="weight"
                        name="weight"
                        label={intl.formatMessage({
                          id: 'DimensionVerificationForm.weightLabel',
                        })}
                        placeholder={intl.formatMessage({
                          id: 'DimensionVerificationForm.weightPlaceholderMessage',
                        })}
                        validate={composeValidators(
                          required(
                            intl.formatMessage({
                              id: 'DimensionVerificationForm.weightRequiredMessage',
                            })
                          ),
                          minValue(
                            intl.formatMessage({
                              id: 'DimensionVerificationForm.minWeightMessage',
                            }),
                            0
                          )
                        )}
                        min={0}
                      />

                      <FieldSelect
                        className={css.formFld}
                        id="massUnit"
                        name="massUnit"
                        label={intl.formatMessage({
                          id: 'EditListingProductForm.massUnitLabelMessage',
                        })}
                        initialValue={'oz'}
                        validate={required(
                          intl.formatMessage({
                            id: 'EditListingProductForm.weightUnitRequiredMessage',
                          })
                        )}
                      >
                        <option value="" disabled>
                          {intl.formatMessage({
                            id: 'ProductOrderForm.selectUnitOptions',
                          })}
                        </option>
                        {unitOfMeasurementOptions?.map(item => (
                          <option key={item.key} value={item.key}>
                            {item.label}
                          </option>
                        ))}
                      </FieldSelect>
                    </div>
                    <p>
                      {intl.formatMessage({
                        id: 'ProductOrderForm.needHelpEstimatingWeight',
                      })}
                      <ExternalLink href="/help/shipping#package-weight">
                        {intl.formatMessage({
                          id: 'ProductOrderForm.seeGuide',
                        })}
                      </ExternalLink>
                    </p>
                  </div>
                )}

                <PrimaryButton
                  className={css.submitButton}
                  type="submit"
                  inProgress={submitInProgress}
                  disabled={submitDisabled}
                  ready={ready}
                >
                  {intl.formatMessage({
                    id: 'DimensionVerificationForm.saveDetails',
                  })}
                </PrimaryButton>
              </div>
            </div>
          </Form>
        );
      }}
    />
  );
};

DimensionVerificationFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  formId: null,
  inProgress: false,
};

const { string } = PropTypes;

DimensionVerificationFormComponent.propTypes = {
  rootClassName: string,
  className: string,
};

const DimensionVerificationForm = compose(injectIntl)(DimensionVerificationFormComponent);

DimensionVerificationForm.displayName = 'DimensionVerificationForm';

export default DimensionVerificationForm;
